import * as React from "react";
import { EventCategory } from "../../../utils/analytics";
import Actionable from "../actionable/actionable";
import { Colors } from "../design-tokens/colors";
import { ChevronIcon } from "../icons/chevron-icon";

import getPager from "./pagination-helper";
import styles from "./paginator.styles";

interface PaginationProps {
  totalPageCount: number;
  currentPageNumber: number;
  maxPagesToDisplay: number;
  onPageChange: (pageNumber: number) => void;
  eventCategory: EventCategory;
}

const Paginator: React.FC<PaginationProps> = (props) => {
  const {
    totalPageCount,
    currentPageNumber,
    maxPagesToDisplay,
    onPageChange,
    eventCategory,
  } = props;

  const pager = getPager(totalPageCount, currentPageNumber, maxPagesToDisplay);
  const pages = pager.pages || [];

  const pageLinks = pages.map((item) => (
    <Actionable
      key={`page-${item}`}
      role="button"
      ariaLabel={`page-${item}`}
      css={[styles.pageButton, item === currentPageNumber && styles.active]}
      onClick={
        totalPageCount > 1 && pager.currentPage !== item
          ? () => onPageChange(item)
          : undefined
      }
      aria-label={`Page ${item}`}
      title={`Page ${item}`}
      type="button"
      analytics={{
        category: eventCategory,
        action: `Click - Pagination`,
        label: `Page ${item}`,
      }}
    >
      {item}
    </Actionable>
  ));

  const isPrevHidden = currentPageNumber === 1;
  const isNextHidden = currentPageNumber === totalPageCount;

  const goToPrev = () => {
    const prevIndex =
      currentPageNumber === 1 ? totalPageCount : currentPageNumber - 1;
    onPageChange(prevIndex);
  };
  const goToNext = () => {
    const nextIndex =
      currentPageNumber === totalPageCount ? 1 : currentPageNumber + 1;
    onPageChange(nextIndex);
  };

  return (
    <div css={styles.paginationWrapper}>
      {!isPrevHidden && (
        <Actionable
          css={styles.pageButton}
          role="button"
          key="page-prev"
          ariaLabel="Previous"
          onClick={goToPrev}
          aria-label="Page prev"
          title="Page prev"
          analytics={{
            category: eventCategory,
            action: `Click - Pagination`,
            label: `Page prev`,
          }}
        >
          <ChevronIcon direction="left" color={Colors.Black} />
        </Actionable>
      )}

      {pageLinks}

      {!isNextHidden && (
        <Actionable
          css={styles.pageButton}
          role="button"
          key="page-next"
          ariaLabel="Next"
          onClick={goToNext}
          aria-label="Page next"
          data-testid="page-next"
          title="Page next"
          analytics={{
            category: eventCategory,
            action: `Click - Pagination`,
            label: `Page next`,
          }}
        >
          <ChevronIcon color={Colors.Black} />
        </Actionable>
      )}
    </div>
  );
};

export default Paginator;
